import { Listbox } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'

export default function Sorter<T extends string, O extends Record<T, string>>({ defaultSort, options, setSort }: {
	defaultSort: T,
	options: O,
	setSort: (sort: keyof O, isDescending: boolean) => void,
}): JSX.Element {
	const [selected, setSelected] = useState<T>(defaultSort)
	const [isDescending, setIsDescending] = useState(true)

	useEffect(() => {
		setSort(selected, isDescending)
	})

	return (
		<Listbox value={selected} onChange={setSelected}>
			<div className="flex flex-wrap -mt-2 -ml-3 space-x-3 sm:mt-0">
				<Listbox.Label as="label" className="leading-7 py-px hidden sm:block">Sort by:</Listbox.Label>

				<div className="relative shrink-0 mt-2 ml-3 sm:mt-0">
					<Listbox.Button className="inline-flex items-center rounded-full py-0.5 border border-gray-200 bg-gradient-to-b from-white via-gray-100 to-gray-300 px-2.5 space-x-1.5 ring-offset-gray-100">
						<span className="font-semibold">{options[selected]}</span>
						<svg className="h-6 w-3" viewBox="0 0 12 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
							<path d="M6.58392 15.5712L11.763 10.3921C11.9154 10.2398 12 10.0367 12 9.81664C12 9.59662 11.9154 9.39351 11.763 9.24118C11.4415 8.91961 10.9337 8.91961 10.6121 9.24118L6.11001 13.7433C6.04231 13.811 5.95769 13.811 5.88999 13.7433L1.38787 9.24118C1.06629 8.9196 0.558533 8.9196 0.236953 9.24118C0.0846261 9.39351 -4.16958e-07 9.59661 -4.26576e-07 9.81664C-4.36193e-07 10.0367 0.0846261 10.2398 0.236953 10.3921L5.41608 15.5712C5.73766 15.8928 6.26234 15.8928 6.58392 15.5712Z" />
						</svg>
					</Listbox.Button>

					<Listbox.Options className="absolute min-w-full whitespace-nowrap top-full mt-1 bg-white shadow-md rounded-xl divide-y divide-gray-200 overflow-hidden focus:ring-0">
						{Object.entries(options).map(option => (
							<Listbox.Option key={option[0]} value={option[0]} as={Fragment}>
								{({ active }) => (
									<li className={`px-2.5 py-0.5 cursor-pointer hover:bg-green-200${active ? ' bg-green-200' : ''}`}>{option[1] as string}</li>
								)}
							</Listbox.Option>
						))}
					</Listbox.Options>
				</div>

				<button className="flex border border-gray-700 rounded-full px-3 py-0.5 mt-2 ring-offset-gray-100 sm:mt-0 sm:space-x-2" onClick={() => setIsDescending(!isDescending)}>
					<span className="font-semibold hidden sm:block">{isDescending ? 'Descending' : 'Ascending'}</span>
					<svg className="h-6" viewBox="0 0 18 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
						{isDescending ? (
							<>
								<g clipPath="url(#descendingClip)">
									<path d="M14.6625 12.4875L12.0375 9.8625C11.9625 9.7875 11.925 9.675 11.925 9.5625C11.925 9.45 11.9625 9.3375 12.0375 9.2625C12.1875 9.1125 12.45 9.1125 12.6375 9.2625L14.55 11.175V6.4125C14.55 6.1875 14.7375 6 14.9625 6C15.1875 6 15.375 6.1875 15.375 6.4125V11.175L17.2875 9.2625C17.4375 9.1125 17.7 9.1125 17.8875 9.2625C17.9625 9.375 18 9.4875 18 9.6C18 9.7125 17.9625 9.825 17.8875 9.9L15.2625 12.525C15.1125 12.6375 14.85 12.6375 14.6625 12.4875Z" />
									<path d="M17.4 16.7625H0.6C0.2625 16.7625 0 17.025 0 17.3625C0 17.7375 0.2625 18 0.6 18H17.3625C17.7 18 17.9625 17.7375 17.9625 17.4C18 17.0625 17.7375 16.7625 17.4 16.7625Z" />
									<path d="M10.0125 11.4H0.6375C0.2625 11.4 0 11.6625 0 12C0 12.3375 0.2625 12.6 0.6 12.6H9.975C10.3125 12.6 10.575 12.3375 10.575 12C10.6125 11.6625 10.35 11.4 10.0125 11.4Z" />
									<path d="M7.875 6H0.6C0.2625 6 0 6.2625 0 6.6V6.6375C0 6.975 0.2625 7.2375 0.6 7.2375H7.9125C8.25 7.2375 8.5125 6.975 8.5125 6.6375C8.5125 6.3 8.2125 6 7.875 6Z" />
								</g>
								<defs>
									<clipPath id="descendingClip">
										<rect width="18" height="12" fill="white" transform="translate(0 6)" />
									</clipPath>
								</defs>
							</>
						) : (
							<>
								<path d="M14.6625 11.5125L12.0375 14.1375C11.9625 14.2125 11.925 14.325 11.925 14.4375C11.925 14.55 11.9625 14.6625 12.0375 14.7375C12.1875 14.8875 12.45 14.8875 12.6375 14.7375L14.55 12.825V17.5875C14.55 17.8125 14.7375 18 14.9625 18C15.1875 18 15.375 17.8125 15.375 17.5875V12.7875L17.2875 14.7C17.4375 14.85 17.7 14.85 17.8875 14.7C17.9625 14.625 18 14.5125 18 14.4C18 14.2875 17.9625 14.175 17.8875 14.1L15.2625 11.475C15.1125 11.3625 14.85 11.3625 14.6625 11.5125Z" />
								<path d="M17.4 7.2375H0.6C0.2625 7.2375 0 6.9375 0 6.6C0 6.2625 0.2625 6 0.6 6H17.3625C17.7375 6 18 6.2625 18 6.6C18 6.9375 17.7375 7.2375 17.4 7.2375Z" />
								<path d="M10.0125 12.6H0.6375C0.2625 12.6 0 12.3375 0 12C0 11.6625 0.2625 11.4 0.6 11.4H9.975C10.3125 11.4 10.575 11.6625 10.575 12C10.6125 12.3375 10.35 12.6 10.0125 12.6Z" />
								<path d="M7.875 18H0.6C0.2625 18 0 17.7375 0 17.4V17.3625C0 17.025 0.2625 16.7625 0.6 16.7625H7.9125C8.25 16.7625 8.5125 17.025 8.5125 17.3625C8.5125 17.7 8.2125 18 7.875 18Z" />
							</>
						)}
					</svg>
				</button>
			</div>
		</Listbox>
	)
}
